import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Mail, Phone, Clock, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { CONTACT } from "@/constants/testIds";
import { CONTACT_INFO, whatsappHref } from "@/constants/contact";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NETLIFY_FORM_NAME = "contact";

const HIRING_OPTIONS = [
  { value: "IT Recruitment", label: "IT Recruitment" },
  { value: "Non-IT Recruitment", label: "Non-IT Recruitment" },
  { value: "Permanent Hiring", label: "Permanent Hiring" },
  { value: "Executive Search", label: "Executive Search" },
  { value: "Bulk Hiring", label: "Bulk Hiring" },
  { value: "Campus Hiring", label: "Campus Hiring" },
  { value: "Contract Staffing", label: "Contract Staffing" },
  { value: "HR Consulting", label: "HR Consulting" },
  { value: "Payroll Services", label: "Payroll Services" },
  { value: "Job Seeker Inquiry", label: "I'm a Job Seeker" },
  { value: "General Inquiry", label: "General Inquiry" },
];

const CONTACT_CARDS = [
  { icon: Phone, title: "Call Us", primary: CONTACT_INFO.callNumberDisplay, sub: "Mon – Sat · 9:30 AM – 6:30 PM", link: CONTACT_INFO.callHrefTel, accent: "royal" },
  { icon: FaWhatsapp, title: "WhatsApp", primary: CONTACT_INFO.whatsappNumberDisplay, sub: "Chat with our team", link: whatsappHref(), accent: "teal", external: true },
  { icon: Mail, title: "Email", primary: CONTACT_INFO.email, sub: "Reach us any time", link: CONTACT_INFO.emailHref, accent: "royal" },
  { icon: MapPin, title: "Location", primary: CONTACT_INFO.location, sub: "Serving pan-India", accent: "teal" },
];

const initial = {
  name: "",
  phone: "",
  email: "",
  company: "",
  hiring_requirement: "IT Recruitment",
  message: "",
  website: "", // honeypot
};

function computeInitial() {
  try {
    const t = new URLSearchParams(window.location.search).get("type");
    if (t === "candidate") return { ...initial, hiring_requirement: "Job Seeker Inquiry" };
    if (t === "employer") return { ...initial, hiring_requirement: "IT Recruitment" };
  } catch {
    // ignore
  }
  return initial;
}

export default function ContactPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(computeInitial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });

  useEffect(() => {
    const type = params.get("type");
    if (type === "candidate") {
      setForm((f) => ({ ...f, hiring_requirement: "Job Seeker Inquiry" }));
    } else if (type === "employer") {
      setForm((f) => ({ ...f, hiring_requirement: "IT Recruitment" }));
    }
  }, [params.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    const phoneClean = form.phone.replace(/[\s\-().]/g, "");
    if (!/^\+?\d{6,15}$/.test(phoneClean)) e.phone = "Please enter a valid phone number.";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Please share a short message (min 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (key) => (ev) => {
    const value = ev?.target ? ev.target.value : ev;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus({ state: "loading", message: "" });

    // Build a WhatsApp-formatted summary of the enquiry.
    const waLines = [
      "*New Enquiry — Ascend Tech Solutions*",
      "",
      `*Name:* ${form.name}`,
      `*Phone:* ${form.phone}`,
      `*Email:* ${form.email}`,
      `*Company:* ${form.company || "—"}`,
      `*Requirement:* ${form.hiring_requirement}`,
      "",
      "*Message:*",
      form.message,
    ];
    const waText = encodeURIComponent(waLines.join("\n"));
    const waUrl = `https://wa.me/${CONTACT_INFO.whatsappNumberIntl}?text=${waText}`;

    try {
      // Fire-and-forget submission to Netlify Forms for a persistent record.
      const body = new URLSearchParams();
      body.append("form-name", NETLIFY_FORM_NAME);
      body.append("name", form.name);
      body.append("phone", form.phone);
      body.append("email", form.email);
      body.append("company", form.company || "");
      body.append("hiring_requirement", form.hiring_requirement);
      body.append("message", form.message);
      body.append("bot-field", form.website || "");
      // Do not block WhatsApp opening on this.
      fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      }).catch(() => {});
    } catch {
      // ignore — WhatsApp handoff below is the primary channel.
    }

    // Open WhatsApp with the pre-formatted message. On mobile, popup blockers
    // often block window.open even from a click handler, so we navigate the
    // current tab directly — this deep-links into the WhatsApp app reliably
    // on both Android and iOS.
    window.location.href = waUrl;
    setStatus({
      state: "success",
      message:
        "Thanks! Your enquiry has been forwarded to us on WhatsApp. We'll get back to you shortly.",
    });
    setForm(initial);
  };

  return (
    <div className="flex flex-col">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy">
        {/* MOBILE (< lg): stacked — full image on top (no crop), text below on solid navy */}
        <div className="lg:hidden">
          <div className="relative w-full aspect-[4/3] bg-brand-navy overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?auto=format&fit=crop&w=1600&q=85"
              alt="HR consultant welcoming a candidate for a professional discussion"
              className="absolute inset-0 w-full h-full object-contain"
              style={{ objectPosition: "center center" }}
              loading="eager"
              draggable={false}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-brand-navy" />
          </div>
          <div className="relative bg-brand-navy px-5 py-10 sm:px-6 sm:py-12 overflow-hidden">
            <div className="absolute -top-8 -right-10 w-48 h-48 rounded-full bg-brand-teal/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-10 w-48 h-48 rounded-full bg-brand-royal/25 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-[11px] font-semibold tracking-[0.22em] uppercase">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Contact Us
              </span>
              <h1 className="mt-5 font-heading font-extrabold text-white text-3xl sm:text-4xl leading-[1.08] tracking-tight">
                Start a Conversation With Our Team
              </h1>
              <p className="mt-4 text-sm sm:text-base text-white/95 leading-relaxed font-medium max-w-lg">
                Whether you are hiring for your organization or exploring a
                professional opportunity, we would be glad to hear from you.
              </p>
            </div>
          </div>
        </div>

        {/* DESKTOP (lg+): full-bleed image with strong overlay */}
        <div className="hidden lg:block relative">
          <div className="absolute inset-0">
            <img src="https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?auto=format&fit=crop&w=1920&q=90" alt="HR consultant welcoming a candidate for a professional discussion" className="w-full h-full object-cover" style={{ objectPosition: "center 35%" }} />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/85 to-brand-royal/75" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-transparent to-brand-navy/50" />
          </div>
          <div className="container-brand relative z-10 py-28 md:py-32">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-xs font-semibold tracking-[0.22em] uppercase">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Contact Us
            </span>
            <h1 className="mt-6 font-heading font-extrabold text-white text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              Start a Conversation With Our Team
            </h1>
            <p className="mt-6 max-w-2xl text-white/95 text-lg leading-relaxed font-medium">
              Whether you are hiring for your organization or exploring a
              professional opportunity, we would be glad to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="section-pad bg-white">
        <div className="container-brand">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CONTACT_CARDS.map((c) => {
              const inner = (
                <>
                  <div className={`p-2.5 rounded-lg w-fit ${c.accent === "teal" ? "bg-brand-teal/10 text-brand-teal" : "bg-brand-royal/10 text-brand-royal"}`}>
                    <c.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-brand-navy">{c.title}</h3>
                  <div className="mt-2 text-sm font-semibold text-brand-text">{c.primary}</div>
                  <div className="mt-1 text-xs text-brand-textSoft">{c.sub}</div>
                </>
              );
              return c.link ? (
                <a key={c.title} href={c.link} target={c.external ? "_blank" : undefined} rel={c.external ? "noopener noreferrer" : undefined} className="card-surface p-6 block hover:border-brand-royal transition-colors">
                  {inner}
                </a>
              ) : (
                <div key={c.title} className="card-surface p-6">{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM SECTION with glassmorphism */}
      <section className="relative overflow-hidden py-14 sm:py-16 md:py-24">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?auto=format&fit=crop&w=1920&q=90" alt="" className="w-full h-full object-cover hidden lg:block" />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/92 to-brand-royal/85 lg:from-brand-navy/85 lg:via-brand-royal/70 lg:to-brand-navy/88" />
        </div>
        <div className="container-brand relative z-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5 text-white">
            <span className="eyebrow-light">Send an Inquiry</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white">
              Tell us about your hiring need or career interest
            </h2>
            <p className="mt-5 text-white/95 text-base sm:text-lg leading-relaxed font-medium">
              Share a few details and our team will respond with next steps.
              Your information is used only for recruitment coordination.
            </p>
            <div className="mt-8 space-y-3">
              <a href={CONTACT_INFO.callHrefTel} className="flex items-center gap-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur-sm p-4 transition-colors">
                <Phone className="w-5 h-5 text-brand-teal" />
                <div>
                  <div className="text-xs text-white/85">Call directly</div>
                  <div className="text-sm font-semibold text-white">{CONTACT_INFO.callNumberDisplay}</div>
                </div>
              </a>
              <a href={whatsappHref()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur-sm p-4 transition-colors">
                <FaWhatsapp className="w-5 h-5 text-[#25D366]" />
                <div>
                  <div className="text-xs text-white/85">Chat on WhatsApp</div>
                  <div className="text-sm font-semibold text-white">{CONTACT_INFO.whatsappNumberDisplay}</div>
                </div>
              </a>
              <a href={CONTACT_INFO.emailHref} className="flex items-center gap-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur-sm p-4 transition-colors">
                <Mail className="w-5 h-5 text-brand-teal" />
                <div>
                  <div className="text-xs text-white/85">Email us</div>
                  <div className="text-sm font-semibold text-white break-all">{CONTACT_INFO.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-3 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm p-4">
                <Clock className="w-5 h-5 text-brand-teal" />
                <div>
                  <div className="text-xs text-white/85">Business hours</div>
                  <div className="text-sm font-semibold text-white">{CONTACT_INFO.hoursShort}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              name={NETLIFY_FORM_NAME}
              method="POST"
              onSubmit={handleSubmit}
              data-testid={CONTACT.form}
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              noValidate
              className="relative rounded-2xl border border-white/25 bg-white/15 backdrop-blur-md p-6 md:p-8 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]"
            >
              <input type="hidden" name="form-name" value={NETLIFY_FORM_NAME} />
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={handleChange("website")} className="hidden" aria-hidden="true" name="bot-field" />

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required error={errors.name} htmlFor="name">
                  <input id="name" name="name" data-testid={CONTACT.fullName} type="text" value={form.name} onChange={handleChange("name")} autoComplete="name" placeholder="e.g., Priya Sharma" className={glassInput(errors.name)} />
                </Field>
                <Field label="Phone Number" required error={errors.phone} htmlFor="phone">
                  <input id="phone" name="phone" data-testid={CONTACT.phone} type="tel" value={form.phone} onChange={handleChange("phone")} autoComplete="tel" placeholder="+91 98XXXXXXXX" className={glassInput(errors.phone)} />
                </Field>
                <Field label="Email Address" required error={errors.email} htmlFor="email">
                  <input id="email" name="email" data-testid={CONTACT.email} type="email" value={form.email} onChange={handleChange("email")} autoComplete="email" placeholder="name@company.com" className={glassInput(errors.email)} />
                </Field>
                <Field label="Company Name" htmlFor="company">
                  <input id="company" name="company" data-testid={CONTACT.company} type="text" value={form.company} onChange={handleChange("company")} autoComplete="organization" placeholder="Optional" className={glassInput(false)} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Hiring Requirement" required htmlFor="hiring">
                    <input type="hidden" name="hiring_requirement" value={form.hiring_requirement} />
                    <Select value={form.hiring_requirement} onValueChange={handleChange("hiring_requirement")}>
                      <SelectTrigger id="hiring" data-testid={CONTACT.service} className="h-11 bg-white/15 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white text-brand-text">
                        {HIRING_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
              <div className="mt-5">
                <Field label="Message" required error={errors.message} htmlFor="message">
                  <textarea id="message" name="message" data-testid={CONTACT.message} value={form.message} onChange={handleChange("message")} rows={5} placeholder="Briefly describe your hiring need or the kind of opportunity you're exploring." className={`${glassInput(errors.message)} h-auto py-3`} />
                </Field>
              </div>

              {status.state === "success" && (
                <div data-testid={CONTACT.success} role="status" className="mt-5 flex items-start gap-3 rounded-lg border border-brand-teal/60 bg-brand-teal/20 backdrop-blur-sm p-4 text-white">
                  <CheckCircle2 className="h-5 w-5 text-brand-teal mt-0.5 shrink-0" />
                  <div className="text-sm">{status.message}</div>
                </div>
              )}
              {status.state === "error" && (
                <div data-testid={CONTACT.error} role="alert" className="mt-5 flex items-start gap-3 rounded-lg border border-red-300/60 bg-red-500/20 backdrop-blur-sm p-4 text-white">
                  <AlertCircle className="h-5 w-5 text-red-200 mt-0.5 shrink-0" />
                  <div className="text-sm">{status.message}</div>
                </div>
              )}

              <button type="submit" data-testid={CONTACT.submit} disabled={status.state === "loading"} className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-brand-teal hover:bg-[#0C8B8E] text-white font-semibold text-sm w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed transition-colors">
                {status.state === "loading" ? "Sending..." : "Send"}
                {status.state !== "loading" && <Send className="h-4 w-4" />}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="section-pad bg-white">
        <div className="container-brand grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="eyebrow">Where We Are</span>
            <h2 className="mt-3 heading-2">Based in Pune. Serving India.</h2>
            <p className="mt-5 body-lg">
              Ascend Tech Solutions is located in Pune, Maharashtra — a leading
              hub for technology, business services and professional talent in
              India.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden border border-brand-border shadow-sm">
              <iframe
                title="Ascend Tech Solutions - Pune Location"
                src="https://www.google.com/maps?q=Pune,%20Maharashtra,%20India&output=embed"
                width="100%"
                height="420"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, error, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block text-sm font-semibold text-white mb-1.5">
        {label} {required && <span className="text-brand-teal">*</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-200">{error}</span>}
    </label>
  );
}

function glassInput(hasError) {
  return `w-full h-11 rounded-md border px-3.5 text-sm text-white placeholder:text-white/70 bg-white/20 focus:bg-white/25 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-colors ${
    hasError ? "border-red-300/70" : "border-white/40"
  }`;
}
